/* eslint-disable no-underscore-dangle */
import axios from "axios";

/**
 * A service for making HTTP requests to our API
 * server. Wraps an axios instance and sets defaults
 * such as provides the JWT with each request.
 */
export default class HttpService {
  /**
   * Create an HttpService instance
   * @param {string} resourcePath Provide a common path to the resource
   */
  constructor ( resourcePath ) {
    this.instance = axios.create( {
      // eslint-disable-next-line no-underscore-dangle
      baseURL: HttpService._createBaseUrl( resourcePath ),
      headers: {
        "Content-Type": "application/json; charset=utf-8"
      },
      withCredentials: true
    } );
  }

  /**
   * Sends a GET request to our API server to the resource
   * at the given path.
   * @param {string} path The relative path to the resource
   * @param {*} params Data to be added to the querystring
   */
  get ( path, params ) {
    return this.instance.get( path, { params } );
  }

  /**
   * Sends a PATCH request to our API server to the resource
   * at the given path.
   * @param {string} path  The relative path to the resource
   * @param {*} payload Any data to send as a part of the request
   * @param {*} params Data to be added to the querystring
   */
  patch ( path, payload, params ) {
    return this.instance.patch( path, payload, { params } );
  }

  /**
   * Sends a POST request to our API server to the resource
   * at the given path.
   * @param {string} path The relative path to the resource
   * @param {*} payload Any data to send as a part of the request
   * @param {*} params Data to be added to the querystring
   * @param config Additional configuration
   */
  post ( path, payload, params, config ) {
    return this.instance.post( path, payload, { params, ...config } );
  }

  /**
   * Sends a PUT request to our API server to the resource
   * at the given path.
   * @param {string} path The relative path to the resource
   * @param {*} payload Any data to send as a part of the request
   * @param {*} params Data to be added to the querystring
   */
  put ( path, payload, params ) {
    return this.instance.put( path, payload, { params } );
  }

  /**
   * Sends a DELETE request to our API server to the resource
   * at the given path.
   * @param {string} path The relative path to the resource
   * @param {*} params Data to be added to the querystring
   */
  delete ( path, params ) {
    return this.instance.delete( path, { params } );
  }

  /**
   * Create the baseUrl that we'll use for all axios requests.
   * @param {*} resourcePath The sub-path for the resource (e.g. forms/)
   */
  static _createBaseUrl ( resourcePath ) {
    let result = "/";

    if ( result.slice( -1 ) !== "/" ) {
      result += "/";
    }

    if ( resourcePath ) {
      // Strip out a trailing slash if given,
      // we'll provide our own to be consistent.
      const trimmedResourcePath = resourcePath.replace( /\/$/, "" );

      result += `${trimmedResourcePath}/`;
    }

    return result;
  }
}
